// content/zones/zone4.ts — THE GLOAMING MINES' zone mechanic.
//
//   DESIGN §5 Zone 4: "sight is a resource — the dark hides everything but
//   tells, light is fuel you spend and defend, and something walks the dark that
//   weapons cannot touch."
//
// Five programs live here, and between them they are the zone:
//
//   1. THE DARKNESS. One offscreen mask, filled near-black over the whole view,
//      with soft holes punched at the Warden's lamp and at every lit brazier.
//      The mask is drawn ABOVE the bodies, so hiding an enemy costs nothing —
//      it is simply not lit. What is drawn back on top of the mask is exactly
//      the counterplay set: eye-glints, every telegraph, every projectile. The
//      anti-Styx guardrail from RESEARCH, made structural: an attack cannot be
//      invisible, because attacks are painted after the dark and not before it.
//   2. THE BRAZIERS. 'B' tiles. E lights one (instantly in the mines, on a 3s
//      hold in the Mother's chamber), it burns 20s, guttering visibly for the
//      last 3, and enemies put it out. Lighting one is the zone's tactical verb.
//   3. THE GLOOM MODULATION. ctx.gloom.rateMul is written every frame: 2x while
//      the Warden stands in full dark, 0.5x inside brazier light, 1x on the lamp
//      alone. Light management IS clock management (DESIGN, literally).
//   4. THE UNLIT. One per room, 45s in, immune to everything the Warden owns.
//      Brazier light halts it; an Emberburst banishes it for 45s. A resource tax
//      with two counterplays — Gauntlet's Death, ours.
//   5. THE ROSTER'S BRAINS. Delvers burrow and erupt, Lampeaters eat your
//      braziers, Glintmoths flee the light and orbit its rim. Registered through
//      game/enemies.ts's custom-brain seam so no shared file learns this zone.
//
// Every literal comes from data/zones/zone4.ts. The numbers written in this file
// are drawing geometry and nothing else.

import type { Draw, Enemy, Room, RunCtx, Vec, ZoneMechanic } from '../../types'
import {
  ZONE4,
  ZONE4_BOSS_ARENA,
  ZONE4_COLORS,
  ZONE4_ENEMIES,
  ZONE4_GENERATORS,
  ZONE4_LIGHT,
  ZONE4_PROJECTILES,
  ZONE4_UNLIT,
} from '../../data/zones/zone4'
import { registerZone, registerZoneContent } from '../../game/zones'
import { moveEnemy, pursue, registerEnemyBrain } from '../../game/enemies'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'
import { emit } from '../../engine/particles'
import { LOGICAL_H, LOGICAL_W } from '../../engine/render'
import { TILE } from '../../game/world'
import { isDown } from '../../engine/input'

const C = ZONE4_COLORS
const L = ZONE4_LIGHT
const BR = ZONE4_LIGHT.brazier
const U = ZONE4_UNLIT

// ===========================================================================
// 0. THE SURFACES RunCtx DOES NOT CARRY
//
// types.ts is frozen. Four things this zone genuinely needs live on the Run
// object handed to us but not on the RunCtx interface, so they are read
// structurally — the pattern zone 2 established for relicFlags (D-020):
//
//   relicFlags       — documented in game/relics.ts as "the contract with zones
//                      2-6"; plain numbers, absent means no relic.
//   flags            — the meta layer's run switches (game/game.ts), where the
//                      Lampwright shrine node parks lampRadiusMul.
//   world.setSolid   — the ONLY write this file makes outside its own state, and
//                      it makes it in one place: the vault door, which DESIGN
//                      says is not usable while its brazier is dark. It can only
//                      ever *subtract* access (see updateVaultDoor).
//   projectilePool   — read-only, so live enemy shots can be painted back on top
//                      of the mask. The counterplay law is not negotiable and
//                      there is no other way to see the pool from content.
// ===========================================================================

interface Projectilish {
  alive: boolean
  friendly: boolean
  pos: Vec
  radius: number
  color: string
  spriteId: string
  rot: number
  def: { kind: string }
}

interface RunInternals {
  relicFlags?: Record<string, number>
  flags?: Record<string, number | boolean>
  world?: {
    isWall(tx: number, ty: number): boolean
    setSolid(tx: number, ty: number, s: boolean): void
    updateFlow(px: number, py: number, dt: number, force?: boolean): void
  }
  projectilePool?: { live: Projectilish[] }
}

const NO_FLAGS: Record<string, number> = {}
function relicFlags(ctx: RunCtx): Record<string, number> {
  return (ctx as unknown as RunInternals).relicFlags ?? NO_FLAGS
}
function metaFlag(ctx: RunCtx, key: string): number {
  const v = (ctx as unknown as RunInternals).flags?.[key]
  return typeof v === 'number' ? v : 1
}

/** The runtime fields on an enemy body that the Enemy interface does not list. */
type Body = Enemy & {
  radius: number
  telegraph: number
  bob: number
  state: number
}

// ===========================================================================
// 1. THE LIGHT — one registry, shared by the mechanic, the brains and the boss
// ===========================================================================

export interface Brazier {
  x: number
  y: number
  tx: number
  ty: number
  lit: boolean
  /** Seconds of burn left. */
  fuel: number
  /** Seconds of the ignition flare left (cosmetic). */
  flare: number
  /** Seconds of smoke left after it went out (cosmetic). */
  smoke: number
}

/** Gameplay radius of a brazier: shrinks smoothly as it gutters, never flickers. */
function brazierRadius(b: Brazier): number {
  if (!b.lit) return 0
  const k = b.fuel < BR.gutterSeconds ? 0.55 + 0.45 * Math.max(0, b.fuel / BR.gutterSeconds) : 1
  return BR.radius * k
}

function falloff(d: number, r: number, core: number): number {
  if (r <= 0 || d >= r) return 0
  const inner = r * core
  if (d <= inner) return 1
  return 1 - (d - inner) / (r - inner)
}

/**
 * The zone's light state, exported because content/bosses/boss4.ts is the other
 * half of this zone and the Mother's entire fight is a question about it.
 */
export const zone4Lights = {
  braziers: [] as Brazier[],
  inArena: false,
  inRespite: false,
  /** Written by the boss (P2's dust storm shrinks the lamp 30%). */
  lampMul: 1,
  /** Live lamp radius in world px, recomputed by the mechanic each frame. */
  lampRadius: L.lampRadius as number,
  lampX: 0,
  lampY: 0,

  litCount(): number {
    let n = 0
    for (let i = 0; i < this.braziers.length; i++) if (this.braziers[i].lit) n++
    return n
  },

  /** Index of the lit brazier nearest (x,y), or -1. */
  nearestLit(x: number, y: number): number {
    let best = -1
    let bd = Infinity
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (!b.lit) continue
      const d = (b.x - x) * (b.x - x) + (b.y - y) * (b.y - y)
      if (d < bd) {
        bd = d
        best = i
      }
    }
    return best
  },

  /** Index of the unlit brazier nearest (x,y) within `reach`, or -1. */
  nearestUnlit(x: number, y: number, reach: number): number {
    let best = -1
    let bd = reach * reach
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (b.lit) continue
      const d = (b.x - x) * (b.x - x) + (b.y - y) * (b.y - y)
      if (d <= bd) {
        bd = d
        best = i
      }
    }
    return best
  },

  /** THE line that matters: is this point inside a *brazier's* light? */
  inBrazierLight(x: number, y: number): boolean {
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (!b.lit) continue
      const r = brazierRadius(b)
      const dx = b.x - x
      const dy = b.y - y
      if (dx * dx + dy * dy <= r * r) return true
    }
    return false
  },

  inLampLight(x: number, y: number): boolean {
    const r = this.lampRadius
    const dx = this.lampX - x
    const dy = this.lampY - y
    return dx * dx + dy * dy <= r * r
  },

  /** 0 = pitch dark, 1 = fully lit. Brightness, for rendering and for brains. */
  lightAt(x: number, y: number): number {
    if (this.inRespite) return 1
    let best = falloff(Math.hypot(this.lampX - x, this.lampY - y), this.lampRadius, L.lampCore)
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (!b.lit) continue
      const v = falloff(Math.hypot(b.x - x, b.y - y), brazierRadius(b), BR.core)
      if (v > best) best = v
    }
    return best
  },

  /** Centre of the light source nearest (x,y), written into `out`; radius returned. */
  nearestSource(x: number, y: number, out: Vec): number {
    let bd = Math.hypot(this.lampX - x, this.lampY - y) - this.lampRadius
    out.x = this.lampX
    out.y = this.lampY
    let br = this.lampRadius
    for (let i = 0; i < this.braziers.length; i++) {
      const b = this.braziers[i]
      if (!b.lit) continue
      const r = brazierRadius(b)
      const d = Math.hypot(b.x - x, b.y - y) - r
      if (d < bd) {
        bd = d
        out.x = b.x
        out.y = b.y
        br = r
      }
    }
    return br
  },
}

/** Everything the boss draws that must survive the darkness mask. */
export interface GlintLane {
  on: boolean
  x0: number
  y0: number
  x1: number
  y1: number
  /** 0..1 telegraph progress; <0 means the rush itself is running. */
  k: number
}

export const arenaFx = {
  active: false,
  lanes: [] as GlintLane[],
  /** P3's wingbeat: 1 -> 0 while the gust blows. */
  gust: 0,
  /** P2's dust-storm ring radius, 0 = off. */
  dustR: 0,
  /** A hit that pinged off her while she was unlit: seconds left, and where. */
  seal: { t: 0, x: 0, y: 0 },
  /** Where her body actually is — the gust rings need an origin. */
  ghost: { on: false, x: 0, y: 0, k: 0 },
}
for (let i = 0; i < 4; i++) arenaFx.lanes.push({ on: false, x0: 0, y0: 0, x1: 0, y1: 0, k: 0 })

// ===========================================================================
// 2. THE ROSTER'S BRAINS
// ===========================================================================

function num(ai: Record<string, unknown>, key: string, d: number): number {
  const v = ai[key]
  return typeof v === 'number' ? v : d
}

const brainDir: Vec = { x: 0, y: 0 }
const srcScratch: Vec = { x: 0, y: 0 }

// --- Delver: the grunt that leaves the floor ---------------------------------
//
// DESIGN: "dead miners that burrow and re-emerge near you, dirt-plume tell."
//
// FLAGGED INTERPRETATION — "untargetable" without being cheap. A body you cannot
// hit and cannot see is a cheap shot; a body you cannot hit but can watch
// crawling toward you is a decision. So the burrow is never invisible: the
// Delver swaps its own spriteId to a dirt plume (a runtime field on the body, so
// no shared code learns anything), the plume travels at 76 px/s — half a
// Warden's walk — and the eruption telegraphs for half a second at the point it
// will actually land, painted ABOVE the darkness mask so the dark cannot hide
// it. tags.invulnerable is on only while it is genuinely under the floor, and
// its touch damage is zeroed for exactly the same window.

const D_APPROACH = 0
const D_WINDUP = 1
const D_STRIKE = 2
const D_RECOVER = 3
const D_SINK = 4
const D_PLUME = 5
const D_ERUPT = 6

registerEnemyBrain('z4_delver', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const reach = num(ai, 'attackRange', 28)
  const cd = num(ai, 'attackCooldown', 1.9)
  const windup = num(ai, 'windup', 0.45)
  const strikeArc = num(ai, 'strikeArc', 1.5)
  const strikeRange = num(ai, 'strikeRange', 44)
  const strikeDmg = num(ai, 'strikeDamage', 1.2)
  const burrowCd = num(ai, 'burrowCooldown', 7.5)
  const burrowMin = num(ai, 'burrowMinRange', 90)
  const burrowMax = num(ai, 'burrowMaxRange', 330)
  const sinkT = num(ai, 'sinkSeconds', 0.45)
  const underT = num(ai, 'burrowSeconds', 2.5)
  const plumeSpeed = num(ai, 'plumeSpeed', 76)
  const eruptWind = num(ai, 'eruptWindup', 0.5)
  const eruptR = num(ai, 'eruptRadius', 54)
  const eruptDmg = num(ai, 'eruptDamage', 1.5)
  const eruptRec = num(ai, 'eruptRecover', 0.7)

  if (typeof e.tags.z4touch !== 'number') e.tags.z4touch = e.touchDamage

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1

  const bcd = typeof e.tags.z4bcd === 'number' ? e.tags.z4bcd : burrowCd * 0.5
  e.tags.z4bcd = Math.max(0, bcd - dt)

  // ---- under the floor
  if (e.state === D_SINK) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT >= sinkT) {
      e.state = D_PLUME
      e.stateT = 0
      e.spriteId = 'z4_plume'
      e.tags.invulnerable = true
      e.tags.z4under = true
      e.touchDamage = 0
      run.sfx('door')
    }
    return
  }

  if (e.state === D_PLUME) {
    pursue(run, e, brainDir)
    moveEnemy(run, e, brainDir.x * plumeSpeed, brainDir.y * plumeSpeed, dt)
    if (run.rngObj.chance(0.4)) {
      emit(e.pos.x, e.pos.y + 6, { count: 2, color: C.plume, speed: 55, life: 0.4, size: 3, grav: 140 })
    }
    if (e.stateT >= underT) {
      e.state = D_ERUPT
      e.stateT = 0
      e.windup = eruptWind
      e.telegraph = eruptWind
      e.tags.z4eruptR = eruptR
      run.sfx('telegraph')
    }
    return
  }

  if (e.state === D_ERUPT) {
    moveEnemy(run, e, 0, 0, dt)
    e.windup -= dt
    if (e.windup <= 0) {
      e.spriteId = e.def.id
      e.tags.invulnerable = false
      e.tags.z4under = false
      e.touchDamage = typeof e.tags.z4touch === 'number' ? e.tags.z4touch : e.touchDamage
      e.tags.z4eruptR = 0
      e.state = D_RECOVER
      e.stateT = 0
      e.cool = eruptRec
      e.tags.z4bcd = burrowCd
      if (dist <= eruptR + run.player.radius) run.damagePlayer(e.touchDamage * eruptDmg, e.def.id)
      run.sfx('generator-die')
      run.shake(4, 0.28)
      emit(e.pos.x, e.pos.y, {
        count: 26,
        color: C.plume,
        speed: 260,
        speedVar: 180,
        life: 0.55,
        lifeVar: 0.25,
        size: 4,
        grav: 320,
        shape: 1,
      })
    }
    return
  }

  // ---- on the surface: an ordinary telegraphed poke
  if (e.state === D_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, 0, 0, dt)
    if (e.windup <= 0) {
      e.state = D_STRIKE
      e.stateT = 0
      const dir = Math.atan2(dy, dx)
      const inArc = dist <= strikeRange + run.player.radius + e.radius
      if (inArc) run.damagePlayer(e.touchDamage * strikeDmg, e.def.id)
      run.sfx('swing')
      emit(e.pos.x + (dx / dist) * 18, e.pos.y + (dy / dist) * 18, {
        count: 5,
        color: '#cfc0a4',
        speed: 120,
        life: 0.18,
        size: 2,
        angle: dir,
        spread: strikeArc,
      })
    }
    return
  }

  if (e.state === D_STRIKE) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > 0.12) {
      e.state = D_RECOVER
      e.stateT = 0
    }
    return
  }

  if (e.state === D_RECOVER) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > 0.4) {
      e.state = D_APPROACH
      e.stateT = 0
      e.cool = Math.max(e.cool, cd)
    }
    return
  }

  pursue(run, e, brainDir)
  const sp = e.speed * speedMul
  moveEnemy(run, e, brainDir.x * sp, brainDir.y * sp, dt)
  if (e.spawnGrace > 0) return
  if (dist < reach + run.player.radius + e.radius && e.cool <= 0) {
    e.state = D_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
    return
  }
  if (e.tags.z4bcd === 0 && dist > burrowMin && dist < burrowMax) {
    e.state = D_SINK
    e.stateT = 0
    e.tags.z4bcd = burrowCd
    run.sfx('dash')
    emit(e.pos.x, e.pos.y + 4, {
      count: 16,
      color: C.plume,
      speed: 150,
      speedVar: 90,
      life: 0.45,
      size: 3,
      grav: 300,
      shape: 1,
    })
  }
})

// --- Lampeater: the priority target -----------------------------------------
//
// DESIGN: "moth-winged crouchers that fly to lit braziers and douse them;
// priority." Its whole job is to undo the player's work, so it is written to be
// *legible*: it always flies at the nearest thing burning, it announces the meal
// with a 1.5s telegraphed channel sitting still on top of the brazier, and if
// nothing is lit it does not stand around — it harasses at range, so it is still
// a body worth killing on the way past.

const E_SEEK = 0
const E_EAT = 1
const E_APPROACH = 2
const E_WINDUP = 3
const E_DART = 4

registerEnemyBrain('z4_lampeater', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const douseRange = num(ai, 'douseRange', 44)
  const channel = num(ai, 'douseChannel', 1.5)
  const douseCd = num(ai, 'douseCooldown', 2.4)
  const harassRange = num(ai, 'harassRange', 150)
  const harassCd = num(ai, 'harassCooldown', 2.6)
  const harassWind = num(ai, 'harassWindup', 0.4)
  const harassSpeed = num(ai, 'harassSpeed', 400)
  const harassTime = num(ai, 'harassTime', 0.26)
  const harassDmg = num(ai, 'harassDamage', 1)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const sp = e.speed * speedMul

  // eating: sat on the fire, wings wide, telegraphing the whole way
  if (e.state === E_EAT) {
    const idx = typeof e.tags.z4target === 'number' ? e.tags.z4target : -1
    const b = zone4Lights.braziers[idx]
    if (!b || !b.lit) {
      e.state = E_SEEK
      e.stateT = 0
      e.tags.z4target = -1
      e.tags.z4eat = 0
      return
    }
    e.tags.z4eat = Math.min(1, e.stateT / channel)
    e.telegraph = Math.max(e.telegraph, 0.001)
    const bx = b.x - e.pos.x
    const by = b.y - 8 - e.pos.y
    moveEnemy(run, e, bx * 2.2, by * 2.2, dt)
    if (run.rngObj.chance(0.5)) {
      emit(b.x, b.y - 10, { count: 2, color: C.moth, speed: 60, life: 0.35, size: 3, shape: 1 })
    }
    if (e.stateT >= channel) {
      douseBrazier(idx, true)
      e.state = E_SEEK
      e.stateT = 0
      e.cool = douseCd
      e.tags.z4target = -1
      e.tags.z4eat = 0
    }
    return
  }

  // the dart: the answer when there is nothing to eat
  if (e.state === E_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, 0, 0, dt)
    if (e.windup <= 0) {
      e.state = E_DART
      e.stateT = 0
      e.tags.z4hit = false
      const ddx = px - e.pos.x
      const ddy = py - e.pos.y
      const l = Math.hypot(ddx, ddy) || 1
      e.kbx = (ddx / l) * harassSpeed
      e.kby = (ddy / l) * harassSpeed
      run.sfx('swing')
    }
    return
  }

  if (e.state === E_DART) {
    moveEnemy(run, e, 0, 0, dt)
    const d = Math.hypot(px - e.pos.x, py - e.pos.y)
    if (e.tags.z4hit !== true && d < e.radius + run.player.radius + 6) {
      e.tags.z4hit = true
      run.damagePlayer(e.touchDamage * harassDmg, e.def.id)
    }
    if (e.stateT > harassTime) {
      e.state = E_APPROACH
      e.stateT = 0
      e.cool = harassCd
    }
    return
  }

  // seek: the nearest fire, always
  const lit = zone4Lights.nearestLit(e.pos.x, e.pos.y)
  if (lit >= 0 && e.cool <= 0 && e.spawnGrace <= 0) {
    const b = zone4Lights.braziers[lit]
    const bx = b.x - e.pos.x
    const by = b.y - e.pos.y
    const d = Math.hypot(bx, by) || 1
    if (d <= douseRange) {
      e.state = E_EAT
      e.stateT = 0
      e.tags.z4target = lit
      e.telegraph = channel
      run.sfx('telegraph')
      return
    }
    e.state = E_SEEK
    if (Math.abs(bx) > 6) e.facing = bx < 0 ? -1 : 1
    moveEnemy(run, e, (bx / d) * sp, (by / d) * sp, dt)
    return
  }

  // nothing burning: harass
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const d = Math.hypot(dx, dy) || 1
  e.state = E_APPROACH
  if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1
  if (d > harassRange) {
    pursue(run, e, brainDir)
    moveEnemy(run, e, brainDir.x * sp, brainDir.y * sp, dt)
  } else {
    const s = Math.sin(e.bob * 2.2)
    moveEnemy(run, e, ((-dy / d) * s - (dx / d) * 0.25) * sp, ((dx / d) * s - (dy / d) * 0.25) * sp, dt)
  }
  if (d < harassRange * 1.1 && e.cool <= 0 && e.spawnGrace <= 0) {
    e.state = E_WINDUP
    e.stateT = 0
    e.windup = harassWind
    e.telegraph = harassWind
    run.sfx('telegraph')
  }
})

// --- Glintmoth: the swarm that will not come into the light ------------------
//
// DESIGN: "spiral at you, *flee* lamp-light edges, so they orbit your radius
// rim." Inside light they are thrown outward, outside it they spiral in, which
// produces the orbit for free.
//
// FLAGGED — why the flee test is a BRIGHTNESS threshold and not the light's
// radius. The first cut fled anything inside the source's radius, and that is
// unshippable for a reason that only shows on the bench: the lamp is centred on
// the Warden, so a 144px lamp plus a 26px rim band means a Glintmoth is repelled
// from 170px out — further than its own 130px lunge. It could therefore never
// attack, never be reached by a melee form, and never die, so a room containing
// one could never clear. (Caught by test/zone4.mjs's full-zone drive sitting in
// room 2 for four minutes with eight live moths.)
//
// So they flee where the light is BRIGHT (>0.75 of full) rather than merely
// present, which leaves a band of dim outer light they will cross — and once
// they commit to a lunge the light rule is off entirely, because the windup and
// the dive return before it. On the landing they are dazed for a beat AT the
// Warden's feet, in the bright core, unable to flee: that daze is the melee
// answer to them, and the reason the swarm is an obstacle rather than a wall.

registerEnemyBrain('z4_glintmoth', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const lungeRange = num(ai, 'lungeRange', 130)
  const lungeCd = num(ai, 'lungeCooldown', 1.6)
  const windup = num(ai, 'windup', 0.26)
  const lungeSpeed = num(ai, 'lungeSpeed', 380)
  const lungeTime = num(ai, 'lungeTime', 0.22)
  const dazeTime = num(ai, 'dazeSeconds', 0.45)
  const fleeMul = num(ai, 'fleeSpeedMul', 1.35)
  const fleeAbove = num(ai, 'fleeAbove', 0.75)
  const spiral = num(ai, 'spiral', 0.85)

  const passive = (relicFlags(run as unknown as RunCtx).glintmothPassive ?? 0) > 0
  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const sp = e.speed * speedMul

  if (e.state === 1) {
    e.windup -= dt
    moveEnemy(run, e, -dx * 0.2, -dy * 0.2, dt)
    if (e.windup <= 0) {
      e.state = 2
      e.stateT = 0
      e.kbx = (dx / dist) * lungeSpeed
      e.kby = (dy / dist) * lungeSpeed
    }
    return
  }
  if (e.state === 2) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.stateT > lungeTime) {
      e.state = 3
      e.stateT = 0
    }
    return
  }
  if (e.state === 3) {
    // dazed where it landed — wings shut, in the light, and hittable
    e.tags.z4scared = 0
    moveEnemy(run, e, Math.cos(e.bob * 9) * 18, Math.sin(e.bob * 11) * 18, dt)
    if (e.stateT > dazeTime) {
      e.state = 0
      e.stateT = 0
      e.cool = lungeCd
    }
    return
  }

  // the light rule, every frame, before anything else it wants to do
  const lit = zone4Lights.lightAt(e.pos.x, e.pos.y)
  if (lit > fleeAbove) {
    zone4Lights.nearestSource(e.pos.x, e.pos.y, srcScratch)
    const ld = Math.hypot(e.pos.x - srcScratch.x, e.pos.y - srcScratch.y)
    const ox = ld > 0.5 ? (e.pos.x - srcScratch.x) / ld : 1
    const oy = ld > 0.5 ? (e.pos.y - srcScratch.y) / ld : 0
    const push = Math.min(1, (lit - fleeAbove) / Math.max(0.05, 1 - fleeAbove))
    const s = Math.sin(e.bob * 2.4 + e.pos.x * 0.02)
    moveEnemy(run, e, (ox * push * fleeMul - oy * s * 0.8) * sp, (oy * push * fleeMul + ox * s * 0.8) * sp, dt)
    e.tags.z4scared = 1
    return
  }
  e.tags.z4scared = 0

  if (passive) {
    moveEnemy(run, e, Math.cos(e.bob * 0.7) * sp * 0.4, Math.sin(e.bob * 1.3) * sp * 0.4, dt)
    return
  }

  // the spiral: flow toward the Warden plus a tangential component
  pursue(run, e, brainDir)
  const s = Math.sin(e.bob * 1.9) * spiral
  moveEnemy(run, e, (brainDir.x - brainDir.y * s) * sp, (brainDir.y + brainDir.x * s) * sp, dt)
  if (dist < lungeRange && e.cool <= 0 && e.spawnGrace <= 0) {
    e.state = 1
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- The Unlit ---------------------------------------------------------------
//
// The brain is only the body: rise, walk, halt, recoil, sink. Every policy
// question — when it arrives, when it is banished, when it comes back — belongs
// to the mechanic, which owns the room. They talk through tags.

const UN_RISE = 0
const UN_WALK = 1
const UN_RECOIL = 2
const UN_SINK = 3

registerEnemyBrain('z4_unlit', (run, e, dt) => {
  const mode = typeof e.tags.z4mode === 'number' ? e.tags.z4mode : UN_RISE
  const t = (typeof e.tags.z4t === 'number' ? e.tags.z4t : 0) + dt
  e.tags.z4t = t

  // D-031: a grey ping on an unkillable body reads as a bug, not a rule. The
  // first time steel actually lands on it, say the counterplay in place —
  // D-029's standard: if a motivated player reports it broken, it is unsaid.
  // (damageEnemy sets hitFlashT on the invulnerable ping; that is the signal.)
  if (e.hitFlashT > 0 && e.tags.saidSteel !== true) {
    e.tags.saidSteel = true
    run.bark('Your steel passes through it. Brazier-light halts the Unlit; an Emberburst banishes it.')
  }

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1

  if (mode === UN_RISE) {
    e.touchDamage = 0
    moveEnemy(run, e, 0, 0, dt)
    if (t >= U.riseSeconds) {
      e.tags.z4mode = UN_WALK
      e.tags.z4t = 0
      e.touchDamage = U.touchDamage
    }
    return
  }

  if (mode === UN_SINK) {
    e.touchDamage = 0
    moveEnemy(run, e, 0, 0, dt)
    if (t >= U.sinkSeconds) e.kill(true)
    return
  }

  if (mode === UN_RECOIL) {
    e.touchDamage = 0
    moveEnemy(run, e, 0, 0, dt) // the impulse set on impact carries it
    if (t >= U.recoilSeconds) {
      e.tags.z4mode = UN_WALK
      e.tags.z4t = 0
      e.touchDamage = U.touchDamage
    }
    return
  }

  // walking. It never lunges and it never hurries.
  e.touchDamage = U.touchDamage
  pursue(run, e, brainDir)
  const ahead = e.radius + 8
  const nx = e.pos.x + brainDir.x * ahead
  const ny = e.pos.y + brainDir.y * ahead
  if (zone4Lights.inBrazierLight(nx, ny) || zone4Lights.inBrazierLight(e.pos.x, e.pos.y)) {
    // DESIGN: it halts at the edge of brazier light. It strains, and it waits.
    e.tags.z4halt = 1
    moveEnemy(run, e, -brainDir.x * 26, -brainDir.y * 26, dt)
    return
  }
  e.tags.z4halt = 0
  moveEnemy(run, e, brainDir.x * e.speed, brainDir.y * e.speed, dt)

  // Touch. The 30 is dealt HERE rather than left to the shared contact pass,
  // because the withdrawal has to zero touchDamage in the same step and
  // game/game.ts runs updateContactDamage *after* the brains — so the recoil
  // was disarming the hit before it could land. (Caught by test/zone4.mjs
  // asserting the HP delta rather than trusting the tag.) damagePlayer still
  // honours i-frames and ?invuln=1, so nothing else about it changes.
  if (dist < e.radius + run.player.radius + 2) {
    run.damagePlayer(U.touchDamage, e.def.id)
    e.tags.z4mode = UN_RECOIL
    e.tags.z4t = 0
    e.touchDamage = 0
    e.kbx = -(dx / dist) * (U.recoilDistance / U.recoilSeconds) * 3.2
    e.kby = -(dy / dist) * (U.recoilDistance / U.recoilSeconds) * 3.2
    run.sfx('player-hurt')
    run.shake(6, 0.4)
    emit(e.pos.x, e.pos.y, { count: 18, color: '#1a1c28', speed: 200, life: 0.5, size: 4, shape: 1 })
  }
})

// ===========================================================================
// 3. THE MECHANIC
// ===========================================================================

const viewR = { x: 0, y: 0, w: 0, h: 0, zoom: 1 }
const probeA: Vec = { x: 0, y: 0 }
const probeB: Vec = { x: 100, y: 0 }
const textAt: Vec = { x: 0, y: 0 }

function viewRect(d: Draw): void {
  const a = d.worldToScreen(probeA)
  const ax = a.x
  const b = d.worldToScreen(probeB)
  const zoom = (b.x - ax) / 100 || 1
  viewR.zoom = zoom
  viewR.x = d.camera.x - TILE * 3
  viewR.y = d.camera.y - TILE * 3
  viewR.w = LOGICAL_W / zoom + TILE * 6
  viewR.h = LOGICAL_H / zoom + TILE * 6
}

/** Module-level so the Lampeater brain can put a fire out without a handle. */
let activeMechanic: GloamingMines | null = null

/**
 * Put a fire out. Exported because the Mother of Lamps is the other half of this
 * zone and dousing is the only thing she really does (content/bosses/boss4.ts).
 */
export function douseBrazier(i: number, byEnemy: boolean): void {
  const b = zone4Lights.braziers[i]
  if (!b || !b.lit) return
  b.lit = false
  b.fuel = 0
  b.smoke = 1.4
  activeMechanic?.onDouse(b, byEnemy)
}

/** Every brazier flares back up. The Mother's death does this: the room exhales. */
export function relightAllBraziers(): void {
  activeMechanic?.relightAll()
}

// --- room floor art (rails, ore veins, strut shadows) ------------------------

let gw = 0
let gh = 0
let floorLayer: HTMLCanvasElement | null = null
/** World points where ore catches the light — drawn ABOVE the mask, twinkling. */
const oreGlints: { x: number; y: number; p: number }[] = []

class GloamingMines implements ZoneMechanic {
  readonly zoneId = 4 as const

  private ctx: RunCtx | null = null
  private anim = 0
  private inArena = false
  private isRespite = false

  // --- the brazier the vault door is waiting on
  private vaultDoors: { tx: number; ty: number }[] = []
  private vaultBrazier = -1
  private vaultForced = false

  // --- the E channel
  private channel = -1
  private channelT = 0
  private channelNeed: number = BR.lightSeconds

  // --- the Unlit
  private unlit: Enemy | null = null
  private unlitTimer = 0
  private banishT = 0
  private droneT = 0
  private lastPotions = 0
  private toldAboutIt = false

  // --- the Gloom composition (see applyGloom)
  private gloomBase = 1
  private gloomFactor = 1
  private gloomWritten = Number.NaN
  private gloomState: 'dark' | 'lamp' | 'brazier' = 'lamp'

  // --- the mask
  private mask: HTMLCanvasElement | null = null
  private maskCtx: CanvasRenderingContext2D | null = null

  // ------------------------------------------------------------- lifecycle

  onZoneStart(ctx: RunCtx): void {
    this.ctx = ctx
    activeMechanic = this
    this.anim = 0
    this.toldAboutIt = false
    zone4Lights.lampMul = 1
    arenaFx.active = false
    // Compose, never stomp: game.ts folds Gloompact and relic gloom modifiers
    // into rateMul once at zone start and nothing resets it per frame, so the
    // value standing here IS the base this zone multiplies (see applyGloom).
    this.gloomBase = ctx.gloom.rateMul
    this.gloomFactor = 1
    this.gloomWritten = Number.NaN
    ctx.bark('The Gloaming Mines. Whatever you cannot see is still walking.')
  }

  onRoomStart(ctx: RunCtx, room: Room): void {
    this.ctx = ctx
    activeMechanic = this
    this.inArena = room.template.id === ZONE4_BOSS_ARENA.id
    this.isRespite = room.isRespite
    zone4Lights.inArena = this.inArena
    zone4Lights.inRespite = this.isRespite
    this.channelNeed = this.inArena ? BR.arenaLightSeconds : BR.lightSeconds
    this.channel = -1
    this.channelT = 0

    this.buildBraziers(ctx)
    this.buildVault(ctx)
    this.buildFloor(ctx, room)

    this.unlit = null
    this.unlitTimer = U.spawnAtSeconds
    this.banishT = 0
    this.droneT = 0
    this.lastPotions = ctx.player.potions

    if (this.inArena || this.isRespite) return
    const line = ROOM_BARKS[Math.min(ROOM_BARKS.length - 1, room.index)]
    if (line) ctx.bark(line)
  }

  onZoneEnd(ctx: RunCtx): void {
    // Hand the Gloom back the way we found it; the next zone re-bases anyway.
    if (!Number.isNaN(this.gloomWritten)) ctx.gloom.rateMul = this.gloomBase
    this.releaseVault(ctx)
    ctx.player.lampRadius = 0
    zone4Lights.braziers.length = 0
    zone4Lights.lampMul = 1
    zone4Lights.inArena = false
    zone4Lights.inRespite = false
    arenaFx.active = false
    floorLayer = null
    oreGlints.length = 0
    gw = 0
    gh = 0
    this.unlit = null
    if (activeMechanic === this) activeMechanic = null
    this.ctx = null
  }

  // ------------------------------------------------------------- room build
  // findGlyphTiles allocates, so it is called exactly twice per room and the
  // results are cached for the frame loop (per-frame allocation discipline).

  private buildBraziers(ctx: RunCtx): void {
    const tiles = ctx.findGlyphTiles('B')
    zone4Lights.braziers.length = 0
    for (const t of tiles) {
      zone4Lights.braziers.push({
        x: t.x,
        y: t.y,
        tx: Math.floor(t.x / TILE),
        ty: Math.floor(t.y / TILE),
        // DESIGN §5 Zone 4: the respite is the one warm room in the zone, and
        // the Mother's chamber opens lit — she is the only thing that puts those
        // out, so every dark brazier in there is legibly her doing (boss4.ts).
        lit: this.isRespite || this.inArena,
        fuel: this.isRespite || this.inArena ? BR.arenaBurnSeconds : 0,
        flare: 0,
        smoke: 0,
      })
    }
  }

  private buildVault(ctx: RunCtx): void {
    this.vaultDoors.length = 0
    this.vaultBrazier = -1
    this.vaultForced = false
    const doors = ctx.findGlyphTiles('V')
    if (doors.length === 0) return
    let mx = 0
    let my = 0
    for (const p of doors) {
      mx += p.x
      my += p.y
      this.vaultDoors.push({ tx: Math.floor(p.x / TILE), ty: Math.floor(p.y / TILE) })
    }
    mx /= doors.length
    my /= doors.length
    // DESIGN §5 Zone 4 Vault: "its door visible only when a specific brazier is
    // lit". The specific one is simply the nearest — authored that way in the
    // room strings, and derived here so a future room cannot forget to say so.
    let bd = Infinity
    for (let i = 0; i < zone4Lights.braziers.length; i++) {
      const b = zone4Lights.braziers[i]
      const d = (b.x - mx) * (b.x - mx) + (b.y - my) * (b.y - my)
      if (d < bd) {
        bd = d
        this.vaultBrazier = i
      }
    }
  }

  private buildFloor(ctx: RunCtx, room: Room): void {
    const map = room.template.map
    gh = map.length
    gw = map[0]?.length ?? 0
    oreGlints.length = 0
    floorLayer = paintFloorLayer(ctx, gw, gh)
  }

  // ---------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.ctx = ctx
    activeMechanic = this
    this.anim += dt

    // 1. the lamp. Meta (Lampwright) and relic (Moth-Eaten Cloak) both multiply,
    //    and the Mother's dust storm divides.
    const lampMul = metaFlag(ctx, 'lampRadiusMul') * (relicFlags(ctx).lampRadiusMul ?? 1) * zone4Lights.lampMul
    const lampR = L.lampRadius * lampMul
    ctx.player.lampRadius = lampR
    zone4Lights.lampRadius = lampR
    zone4Lights.lampX = ctx.player.pos.x
    zone4Lights.lampY = ctx.player.pos.y

    // 2. the fires
    this.updateBraziers(ctx, dt)
    this.updateChannel(ctx, dt)

    // 3. the Gloom modulation — the zone's headline rule
    this.updateGloom(ctx)

    // 4. the thing in the dark
    this.updateUnlit(ctx, dt)

    // 5. the vault door, which is not a door until its brazier says so
    this.updateVaultDoor(ctx)
  }

  private updateBraziers(ctx: RunCtx, dt: number): void {
    const list = zone4Lights.braziers
    for (let i = 0; i < list.length; i++) {
      const b = list[i]
      if (b.flare > 0) b.flare -= dt
      if (b.smoke > 0) b.smoke -= dt
      if (!b.lit) continue
      b.fuel -= dt
      if (b.fuel <= 0) {
        b.lit = false
        b.fuel = 0
        b.smoke = 1.4
        this.onDouse(b, false)
        continue
      }
      if (ctx.rng() < (b.fuel < BR.gutterSeconds ? 0.5 : 0.18)) {
        emit(b.x, b.y - 12, {
          count: 1,
          color: b.fuel < BR.gutterSeconds ? C.emberDead : C.ember,
          speed: 26,
          life: 0.8,
          size: 2,
          grav: -30,
          shape: 1,
        })
      }
    }
  }

  /** Called for every extinction, whoever caused it. */
  onDouse(b: Brazier, byEnemy: boolean): void {
    emit(b.x, b.y - 8, {
      count: byEnemy ? 20 : 10,
      color: '#2a2b35',
      speed: byEnemy ? 130 : 70,
      speedVar: 60,
      life: 0.9,
      lifeVar: 0.4,
      size: 4,
      grav: -40,
      shape: 1,
    })
    const ctx = this.ctx
    if (!ctx) return
    ctx.sfx('door')
    if (byEnemy) {
      ctx.shake(2.5, 0.25)
      ctx.bark('It took the light. Kill it, or light another.')
    }
  }

  private light(ctx: RunCtx, i: number): void {
    const b = zone4Lights.braziers[i]
    if (!b || b.lit) return
    b.lit = true
    b.fuel = this.inArena ? BR.arenaBurnSeconds : BR.burnSeconds
    b.flare = 0.45
    b.smoke = 0
    ctx.sfx('potion')
    ctx.sfx('pickup')
    ctx.shake(1.6, 0.2)
    emit(b.x, b.y - 10, {
      count: 30,
      color: C.lampHot,
      speed: 190,
      speedVar: 130,
      life: 0.6,
      lifeVar: 0.25,
      size: 4,
      grav: -90,
      shape: 1,
    })
    emit(b.x, b.y - 6, { count: 14, color: C.ember, speed: 90, life: 0.9, size: 3, grav: -60 })
    if (!this.toldAboutIt) {
      this.toldAboutIt = true
      ctx.bark('Light. Twenty seconds of it — and the deep fills slower while it burns.')
    }
  }

  /**
   * E. In the mines the flick is instant (0.3s of flare); in the Mother's
   * chamber it is a 3s hold, and that hold is the fight's tension — you are
   * standing still, in the open, in the one place she is coming to.
   */
  onInteract(ctx: RunCtx): boolean {
    const i = zone4Lights.nearestUnlit(ctx.player.pos.x, ctx.player.pos.y, BR.reach)
    if (i < 0) return false
    if (!this.inArena) {
      this.light(ctx, i)
      return true
    }
    if (this.channel !== i) {
      this.channel = i
      this.channelT = 0
      ctx.sfx('ui')
    }
    return true
  }

  private updateChannel(ctx: RunCtx, dt: number): void {
    if (!this.inArena) {
      this.channel = -1
      return
    }
    const held = isDown('interact')
    if (this.channel < 0) {
      if (!held) return
      const i = zone4Lights.nearestUnlit(ctx.player.pos.x, ctx.player.pos.y, BR.reach)
      if (i < 0) return
      this.channel = i
      this.channelT = 0
    }
    const b = zone4Lights.braziers[this.channel]
    const inReach =
      b != null && !b.lit && Math.hypot(b.x - ctx.player.pos.x, b.y - ctx.player.pos.y) <= BR.reach + 8
    if (!held || !inReach) {
      if (this.channelT > 0.2) ctx.sfx('ui')
      this.channel = -1
      this.channelT = 0
      return
    }
    this.channelT += dt
    if (ctx.rng() < 0.4) {
      emit(b.x, b.y - 6, { count: 1, color: C.ember, speed: 40, life: 0.4, size: 2, shape: 1 })
    }
    if (this.channelT >= this.channelNeed) {
      this.light(ctx, this.channel)
      this.channel = -1
      this.channelT = 0
    }
  }

  /**
   * FLAGGED — how this zone writes ctx.gloom.rateMul without stomping anything.
   *
   * game/gloom.ts does NOT reset rateMul per frame: game.ts sets it once at zone
   * start (Gloompact), game/relics.ts scales it in place when a gloom relic is
   * taken mid-zone, and it is otherwise left alone. So a zone that writes it
   * every frame must (a) know the base and (b) not eat a later relic's change.
   * Both fall out of one invariant: remember what we wrote. If the value we find
   * is not the value we left, something else scaled it, and dividing our own
   * factor back out recovers the new base exactly.
   */
  private applyGloom(ctx: RunCtx, factor: number): void {
    const cur = ctx.gloom.rateMul
    if (cur !== this.gloomWritten) this.gloomBase = this.gloomFactor !== 0 ? cur / this.gloomFactor : cur
    this.gloomFactor = factor
    this.gloomWritten = this.gloomBase * factor
    ctx.gloom.rateMul = this.gloomWritten
  }

  /**
   * FLAGGED INTERPRETATION — what "in darkness" means, and why it is not "your
   * feet are unlit".
   *
   * DESIGN §5 Zone 4: "meter fills 2x in darkness, 0.5x in brazier light. Light
   * management IS clock management." The obvious implementation — read the light
   * at the Warden's tile — has a fatal flaw that only shows up on the bench: the
   * lamp is CENTRED ON THE WARDEN, so the Warden is inside their own lamp on
   * every frame of the zone, and the 2x rate is unreachable. The headline rule
   * of the zone would never once fire in a shipped run. (Caught by test/zone4.mjs
   * asserting the multiplier directly rather than trusting the branch existed.)
   *
   * So the meter answers THE ROOM, not the tile. Your lamp is yours; it is not
   * light in the mine.
   *
   *   0.5x  standing inside a lit brazier's radius     — you are in the fire
   *   1.0x  a brazier burns somewhere, but not on you  — the room is half yours
   *   2.0x  nothing in this room is burning            — the deep owns it
   *
   * All three are reachable, all three are the DESIGN numbers, and the rule is
   * strictly better play: lighting a brazier pays even when you have to walk
   * away from it, so "light is fuel you spend and DEFEND" is on the clock — and
   * a Lampeater eating your last fire doubles it, which is exactly the threat
   * that body is supposed to be.
   */
  private updateGloom(ctx: RunCtx): void {
    let factor: number
    if (zone4Lights.inBrazierLight(ctx.player.pos.x, ctx.player.pos.y)) {
      this.gloomState = 'brazier'
      factor = L.gloom.brazier
    } else if (this.isRespite || zone4Lights.litCount() > 0) {
      this.gloomState = 'lamp'
      factor = L.gloom.lamp
    } else {
      this.gloomState = 'dark'
      factor = L.gloom.dark
    }
    this.applyGloom(ctx, factor)
  }

  // ------------------------------------------------------------- the Unlit

  private updateUnlit(ctx: RunCtx, dt: number): void {
    const potions = ctx.player.potions
    const quaffed = potions < this.lastPotions
    this.lastPotions = potions

    // DESIGN: it does not enter the boss arena, and the respite is safe.
    if (this.inArena || this.isRespite) return

    const live = this.unlit != null && this.unlit.alive
    if (live && quaffed) {
      const u = this.unlit as Enemy
      const d = Math.hypot(u.pos.x - ctx.player.pos.x, u.pos.y - ctx.player.pos.y)
      if (d <= U.banishRadius) this.banish(ctx, u)
    }

    if (this.banishT > 0) {
      this.banishT -= dt
      if (this.banishT <= 0 && !ctx.room.cleared) this.spawnUnlit(ctx, true)
      return
    }

    if (!live) {
      this.unlit = null
      if (ctx.room.cleared) return
      this.unlitTimer -= dt
      if (this.unlitTimer <= 0) this.spawnUnlit(ctx, false)
      return
    }

    // the drone: the cue's RATE is the distance, because engine/audio.ts has no
    // continuous voice and this file is not allowed to add one.
    const u = this.unlit as Enemy
    const d = Math.hypot(u.pos.x - ctx.player.pos.x, u.pos.y - ctx.player.pos.y)
    const k = Math.max(0, Math.min(1, 1 - d / U.droneRange))
    this.droneT -= dt
    if (this.droneT <= 0) {
      this.droneT = U.droneFar + (U.droneNear - U.droneFar) * k
      ctx.sfx('door')
    }
  }

  private spawnUnlit(ctx: RunCtx, returning: boolean): void {
    if (gw === 0) return
    const px = ctx.player.pos.x
    const py = ctx.player.pos.y
    // a dark place, far from the Warden: sample the room and take the best
    let bx = -1
    let by = -1
    let bs = -1
    for (let i = 0; i < 220; i++) {
      const tx = 1 + Math.floor(ctx.rng() * (gw - 2))
      const ty = 1 + Math.floor(ctx.rng() * (gh - 2))
      if (ctx.isWall(tx, ty)) continue
      const x = tx * TILE + TILE / 2
      const y = ty * TILE + TILE / 2
      const d = Math.hypot(x - px, y - py)
      if (d < U.spawnMinDistance) continue
      const score = d - zone4Lights.lightAt(x, y) * 900
      if (score > bs) {
        bs = score
        bx = x
        by = y
      }
    }
    if (bx < 0) {
      this.unlitTimer = 6
      return
    }
    srcScratch.x = bx
    srcScratch.y = by
    const e = ctx.spawnEnemy(U.defId, srcScratch, { elite: null, silent: true })
    if (!e.alive) {
      this.unlitTimer = 6
      return
    }
    // The tag contract (types.ts): weapons do 0, knockback does nothing, and it
    // is not a body the room-clear condition counts.
    //
    // FLAGGED — gloomspawn on a thing that is not gloomspawn. That tag is the
    // engine's only "does not hold the room hostage" switch, and the Unlit
    // CANNOT be killed, so without it every room in this zone would be
    // unclearable from second 45. It also buys two behaviours DESIGN wants: the
    // Unlit dissolves when the room clears ("it withdraws"), and
    // Snapshot.realEnemies reports it as chaff, so a bot never chases something
    // it can never kill. Named here so the reading is on the record.
    e.tags.invulnerable = true
    e.tags.noKnockback = true
    e.tags.gloomspawn = true
    e.tags.z4unlit = true
    e.tags.z4mode = UN_RISE
    e.tags.z4t = 0
    this.unlit = e
    emit(bx, by, { count: 22, color: '#101220', speed: 130, life: 0.8, size: 5, shape: 1 })
    ctx.sfx('death')
    ctx.shake(3, 0.5)
    ctx.bark(
      returning
        ? 'It is back. It was always going to be back.'
        : 'The Unlit walks. Steel will not answer it — light will.',
    )
  }

  private banish(ctx: RunCtx, u: Enemy): void {
    u.tags.z4mode = UN_SINK
    u.tags.z4t = 0
    this.banishT = U.banishSeconds
    this.unlit = null
    ctx.featTick('unlitBanished')
    ctx.sfx('potion')
    ctx.shake(5, 0.4)
    emit(u.pos.x, u.pos.y, {
      count: 34,
      color: C.lampHot,
      speed: 240,
      speedVar: 160,
      life: 0.7,
      lifeVar: 0.3,
      size: 4,
      shape: 1,
    })
    ctx.bark('The burst puts it down. Forty-five seconds, and not one more.')
  }

  // --------------------------------------------------------------- the vault

  /**
   * DESIGN §5 Zone 4: the vault door is "visible only when a specific brazier is
   * lit". Implemented as a strict SUBTRACTION of access: while that brazier is
   * dark this file makes the door tile solid, and while it burns it hands the
   * tile straight back to game/rooms.ts, which owns the timed-vault rule. So it
   * can never open a vault the clock has closed, and it never shuts on a Warden
   * who is already inside.
   */
  private updateVaultDoor(ctx: RunCtx): void {
    if (this.vaultDoors.length === 0) return
    const world = (ctx as unknown as RunInternals).world
    if (!world) return
    const revealed = this.vaultBrazier < 0 || zone4Lights.braziers[this.vaultBrazier]?.lit === true

    const px = ctx.player.pos.x
    const py = ctx.player.pos.y
    // The anti-trap guard, and ONLY the anti-trap guard: a Warden standing on
    // the alcove floor or in the doorway itself keeps their way out even if the
    // brazier gutters behind them. It deliberately does not extend to "near the
    // door" — that version opened the sealed door for anyone who walked up to
    // it, which deletes the rule (caught by test/zone4.mjs walking into a dark
    // vault door and getting in).
    let inside = ctx.tileAt(ctx.player.pos) === 'v'
    if (!inside) {
      const ptx = Math.floor(px / TILE)
      const pty = Math.floor(py / TILE)
      for (const d of this.vaultDoors) {
        if (d.tx === ptx && d.ty === pty) {
          inside = true
          break
        }
      }
    }

    const wantSolid = !revealed && !inside ? true : !ctx.room.isVaultOpen
    let dirty = false
    for (const d of this.vaultDoors) {
      if (world.isWall(d.tx, d.ty) !== wantSolid) {
        world.setSolid(d.tx, d.ty, wantSolid)
        dirty = true
      }
    }
    this.vaultForced = wantSolid && !revealed
    if (dirty) world.updateFlow(px, py, 0, true)
  }

  private releaseVault(ctx: RunCtx): void {
    if (!this.vaultForced) return
    const world = (ctx as unknown as RunInternals).world
    if (!world) return
    for (const d of this.vaultDoors) world.setSolid(d.tx, d.ty, !ctx.room.isVaultOpen)
    this.vaultForced = false
  }

  // ---------------------------------------------------------------- render

  /** Under the bodies: the mine's floor art, and the brazier fixtures. */
  renderUnder(d: Draw): void {
    if (gw === 0) return
    const ctx = d.ctx
    viewRect(d)
    if (floorLayer) {
      const sx = Math.max(0, Math.floor(viewR.x))
      const sy = Math.max(0, Math.floor(viewR.y))
      const sw = Math.min(floorLayer.width - sx, Math.ceil(viewR.w))
      const sh = Math.min(floorLayer.height - sy, Math.ceil(viewR.h))
      if (sw > 0 && sh > 0) ctx.drawImage(floorLayer, sx, sy, sw, sh, sx, sy, sw, sh)
    }
    for (const b of zone4Lights.braziers) {
      const id = b.lit ? (b.fuel < BR.gutterSeconds ? 'z4_brazier_gutter' : 'z4_brazier_lit') : 'z4_brazier_unlit'
      drawSprite(ctx, id, b.x, b.y - 4)
    }
  }

  /**
   * Over the bodies: the dark, and then everything the dark is not allowed to
   * take — tells, glints, projectiles, and the Unlit.
   */
  renderOver(d: Draw): void {
    const run = this.ctx
    if (!run || gw === 0) return
    viewRect(d)
    if (!this.isRespite) this.renderMask(d)
    this.renderOre(d)
    this.renderBrazierTops(d, run)
    this.renderTells(d, run)
    this.renderProjectiles(d, run)
    this.renderUnlit(d, run)
    if (arenaFx.active) this.renderArena(d)
    this.renderPrompts(d, run)
  }

  /** The mask: one offscreen canvas, filled dark, holes punched out of it. */
  private renderMask(d: Draw): void {
    const w = Math.ceil(viewR.w)
    const h = Math.ceil(viewR.h)
    if (!this.mask || this.mask.width < w || this.mask.height < h) {
      this.mask = document.createElement('canvas')
      this.mask.width = Math.max(w, 1024)
      this.mask.height = Math.max(h, 640)
      this.maskCtx = this.mask.getContext('2d')
    }
    const m = this.maskCtx
    if (!m || !this.mask) return
    const ox = viewR.x
    const oy = viewR.y

    m.setTransform(1, 0, 0, 1, 0, 0)
    m.globalCompositeOperation = 'source-over'
    m.clearRect(0, 0, this.mask.width, this.mask.height)
    m.fillStyle = C.dark
    m.globalAlpha = 0.965
    m.fillRect(0, 0, w, h)
    m.globalAlpha = 1
    m.globalCompositeOperation = 'destination-out'

    const hole = (x: number, y: number, r: number, core: number, strength: number): void => {
      if (r <= 0) return
      const cx = x - ox
      const cy = y - oy
      if (cx < -r || cy < -r || cx > w + r || cy > h + r) return
      const g = m.createRadialGradient(cx, cy, Math.max(1, r * core * 0.6), cx, cy, r)
      g.addColorStop(0, `rgba(0,0,0,${strength})`)
      g.addColorStop(0.62, `rgba(0,0,0,${strength * 0.72})`)
      g.addColorStop(1, 'rgba(0,0,0,0)')
      m.fillStyle = g
      m.beginPath()
      m.arc(cx, cy, r, 0, Math.PI * 2)
      m.fill()
    }

    // the Warden's lamp, breathing very slightly so it feels carried
    hole(
      zone4Lights.lampX,
      zone4Lights.lampY,
      zone4Lights.lampRadius * (1 + 0.015 * Math.sin(this.anim * 2.1)),
      L.lampCore,
      1,
    )

    for (const b of zone4Lights.braziers) {
      if (!b.lit) continue
      const gut = b.fuel < BR.gutterSeconds
      const flick = gut
        ? 0.72 + 0.28 * Math.sin(this.anim * 21 + b.tx) * Math.sin(this.anim * 7.3 + b.ty)
        : 1 + 0.03 * Math.sin(this.anim * 9 + b.tx * 1.7)
      hole(b.x, b.y - 6, brazierRadius(b) * flick, BR.core, 1)
      if (b.flare > 0) hole(b.x, b.y - 6, BR.radius * (1.9 - b.flare), 0.2, 0.85)
    }

    // Live enemy shots carry their own small light — a bolt in a mine is a lamp.
    const pool = (this.ctx as unknown as RunInternals).projectilePool
    if (pool) {
      const list = pool.live
      for (let i = 0; i < list.length; i++) {
        const p = list[i]
        if (!p.alive || p.friendly) continue
        hole(p.pos.x, p.pos.y, 40, 0.15, 0.72)
      }
    }

    m.globalCompositeOperation = 'source-over'
    d.ctx.drawImage(this.mask, 0, 0, w, h, ox, oy, w, h)
  }

  /** Pale ore, catching whatever light there is. Visible even in the black. */
  private renderOre(d: Draw): void {
    const ctx = d.ctx
    const t = this.anim
    ctx.save()
    for (let i = 0; i < oreGlints.length; i++) {
      const o = oreGlints[i]
      if (o.x < viewR.x || o.y < viewR.y || o.x > viewR.x + viewR.w || o.y > viewR.y + viewR.h) continue
      const ph = Math.sin(t * (1.1 + o.p * 0.9) + o.p * 6.283)
      if (ph < 0.55) continue
      const k = (ph - 0.55) / 0.45
      ctx.globalAlpha = 0.16 + 0.5 * k
      ctx.fillStyle = C.ore
      ctx.fillRect(Math.round(o.x), Math.round(o.y), 2, 2)
      ctx.globalAlpha = 0.1 * k
      ctx.beginPath()
      ctx.arc(o.x + 1, o.y + 1, 7, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
  }

  /**
   * The fires themselves, painted after the dark: a lit brazier is a beacon, and
   * an unlit one still has to be findable or the zone is a guessing game. Cold
   * coals keep a dull ember so "there is a brazier there" is always readable.
   */
  private renderBrazierTops(d: Draw, run: RunCtx): void {
    const ctx = d.ctx
    const t = this.anim
    const px = run.player.pos.x
    const py = run.player.pos.y
    ctx.save()
    for (const b of zone4Lights.braziers) {
      const x = b.x
      const y = b.y - 10
      if (b.lit) {
        const gut = b.fuel < BR.gutterSeconds
        const flick = gut ? 0.45 + 0.55 * Math.abs(Math.sin(t * 17 + b.tx)) : 0.82 + 0.18 * Math.sin(t * 7 + b.ty)
        const r = 13 * flick
        ctx.globalAlpha = 0.85
        ctx.fillStyle = C.ember
        ctx.beginPath()
        ctx.ellipse(x, y, r * 0.62, r, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
        ctx.fillStyle = C.lampHot
        ctx.beginPath()
        ctx.ellipse(x, y + 2, r * 0.32, r * 0.55, 0, 0, Math.PI * 2)
        ctx.fill()
        // the fuel ring: a burn-down clock you read off the fire itself
        if (!this.inArena && !this.isRespite) {
          const frac = Math.max(0, Math.min(1, b.fuel / BR.burnSeconds))
          ctx.globalAlpha = gut ? 0.55 + 0.35 * Math.sin(t * 16) : 0.4
          ctx.strokeStyle = gut ? '#ff7a42' : C.lamp
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.arc(b.x, b.y - 4, 17, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2)
          ctx.stroke()
        }
      } else {
        // Dead coals. A cold brazier still has to be FINDABLE from across a
        // black room or the zone is a guessing game, so it keeps a dull ember
        // and a thin warm ring — warm, so it never reads as one more ore glint.
        ctx.globalAlpha = 0.4 + 0.16 * Math.sin(t * 1.7 + b.tx)
        ctx.fillStyle = C.emberDead
        ctx.beginPath()
        ctx.ellipse(x, y + 4, 6, 3.5, 0, 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 0.26 + 0.1 * Math.sin(t * 2.3 + b.ty)
        ctx.strokeStyle = C.lamp
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(b.x, b.y - 4, 13, 0, Math.PI * 2)
        ctx.stroke()
        ctx.globalAlpha = 0.1
        ctx.fillStyle = C.lamp
        ctx.beginPath()
        ctx.arc(b.x, b.y - 4, 9, 0, Math.PI * 2)
        ctx.fill()
        if (b.smoke > 0) {
          ctx.globalAlpha = 0.3 * b.smoke
          ctx.fillStyle = '#39404f'
          ctx.beginPath()
          ctx.ellipse(x, y - 10 - (1.4 - b.smoke) * 22, 7, 11, 0, 0, Math.PI * 2)
          ctx.fill()
        }
      }
      // the prompt, when you are close enough for E to mean something
      if (!b.lit && Math.hypot(b.x - px, b.y - py) < BR.reach + 26) {
        ctx.globalAlpha = 1
        textAt.x = b.x
        textAt.y = b.y - 34
        d.text(this.inArena ? 'HOLD E — LIGHT' : 'E — LIGHT', textAt, 11, C.lamp)
      }
    }
    ctx.restore()
  }

  /**
   * The counterplay set, painted after the dark. THE rule of this zone: an enemy
   * you cannot see still shows you its eyes, and always shows you its windup.
   */
  private renderTells(d: Draw, run: RunCtx): void {
    const ctx = d.ctx
    const t = this.anim
    const list = run.enemies
    ctx.save()
    for (let i = 0; i < list.length; i++) {
      const e = list[i] as Body
      if (!e.alive || e.tags.z4unlit) continue
      const lit = this.isRespite ? 1 : zone4Lights.lightAt(e.pos.x, e.pos.y)
      const hidden = 1 - Math.min(1, lit / 0.5)

      // 1. eye-glints — the "something is there" read, fading out as light rises
      if (hidden > 0.02) {
        const eye = e.def.id === 'glintmoth' ? '#9fe8ff' : e.def.id === 'lampeater' ? '#ffe3a0' : '#ffb066'
        const off = e.radius * 0.34
        const yy = e.pos.y - e.radius * 0.55 + Math.sin(e.bob * 7 + i) * 0.8
        ctx.globalAlpha = 0.5 + 0.45 * hidden
        ctx.fillStyle = eye
        ctx.fillRect(Math.round(e.pos.x - off - 1), Math.round(yy), 2, 2)
        ctx.fillRect(Math.round(e.pos.x + off - 1), Math.round(yy), 2, 2)
        ctx.globalAlpha = 0.12 * hidden
        ctx.beginPath()
        ctx.arc(e.pos.x, yy + 1, 9, 0, Math.PI * 2)
        ctx.fill()
      }

      // 2. every windup, at full strength, whatever the light is doing
      if (e.telegraph > 0) {
        const k = 1 - Math.min(1, e.telegraph / 0.6)
        ctx.globalAlpha = 0.35 + 0.45 * k
        ctx.strokeStyle = '#ffd166'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, e.radius + 8 - k * 4, 0, Math.PI * 2)
        ctx.stroke()
      }

      // 3. the Delver's eruption: the AoE is drawn where it will land
      const er = typeof e.tags.z4eruptR === 'number' ? e.tags.z4eruptR : 0
      if (er > 0) {
        const k = 1 - Math.min(1, e.telegraph / 0.5)
        ctx.globalAlpha = 0.25 + 0.45 * k
        ctx.strokeStyle = '#ff9a4a'
        ctx.lineWidth = 2 + k * 2
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, er, 0, Math.PI * 2)
        ctx.stroke()
        ctx.globalAlpha = 0.1 + 0.22 * k
        ctx.fillStyle = '#ff9a4a'
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, er * k, 0, Math.PI * 2)
        ctx.fill()
      }

      // 4. a travelling plume is a threat: it gets its own dirt ring
      if (e.tags.z4under) {
        ctx.globalAlpha = 0.4 + 0.2 * Math.sin(t * 11 + i)
        ctx.strokeStyle = C.plume
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.ellipse(e.pos.x, e.pos.y + 5, 15 + Math.sin(t * 8) * 2, 7, 0, 0, Math.PI * 2)
        ctx.stroke()
      }

      // 5. the Lampeater eating: a line to the fire it is taking, and a clock
      const eat = typeof e.tags.z4eat === 'number' ? e.tags.z4eat : 0
      const tgt = typeof e.tags.z4target === 'number' ? e.tags.z4target : -1
      if (eat > 0 && tgt >= 0) {
        const b = zone4Lights.braziers[tgt]
        if (b) {
          ctx.globalAlpha = 0.5 + 0.3 * Math.sin(t * 18)
          ctx.strokeStyle = C.moth
          ctx.lineWidth = 3
          ctx.setLineDash([6, 5])
          ctx.beginPath()
          ctx.moveTo(e.pos.x, e.pos.y)
          ctx.lineTo(b.x, b.y - 8)
          ctx.stroke()
          ctx.setLineDash([])
          ctx.globalAlpha = 0.85
          ctx.strokeStyle = '#ff7a42'
          ctx.lineWidth = 3
          ctx.beginPath()
          ctx.arc(b.x, b.y - 6, 20, -Math.PI / 2, -Math.PI / 2 + eat * Math.PI * 2)
          ctx.stroke()
        }
      }
    }
    ctx.restore()
  }

  /**
   * DESIGN: "projectiles remain fully visible". They are drawn under the mask
   * like everything else, so they are drawn again on top of it.
   */
  private renderProjectiles(d: Draw, run: RunCtx): void {
    const pool = (run as unknown as RunInternals).projectilePool
    if (!pool) return
    const ctx = d.ctx
    const list = pool.live
    ctx.save()
    for (let i = 0; i < list.length; i++) {
      const p = list[i]
      if (!p.alive || p.friendly) continue
      ctx.globalAlpha = 0.4
      ctx.fillStyle = p.color
      ctx.beginPath()
      ctx.arc(p.pos.x, p.pos.y, p.radius + 4, 0, Math.PI * 2)
      ctx.fill()
      ctx.globalAlpha = 1
      drawSprite(ctx, p.spriteId, p.pos.x, p.pos.y, { rot: p.def.kind === 'straight' ? p.rot : 0 })
    }
    ctx.restore()
  }

  /**
   * The Unlit, painted last and painted always: a shape darker than the dark,
   * two pale eyes, and a ring on the floor that tightens as it closes. It has to
   * be legible in a black room without ever being *lit*.
   */
  private renderUnlit(d: Draw, run: RunCtx): void {
    const u = this.unlit
    if (!u || !u.alive) return
    const ctx = d.ctx
    const t = this.anim
    const mode = typeof u.tags.z4mode === 'number' ? u.tags.z4mode : UN_WALK
    const ut = typeof u.tags.z4t === 'number' ? u.tags.z4t : 0
    let k = 1
    if (mode === UN_RISE) k = Math.min(1, ut / U.riseSeconds)
    else if (mode === UN_SINK) k = Math.max(0, 1 - ut / U.sinkSeconds)

    const x = u.pos.x
    const y = u.pos.y
    const h = 54 * k

    ctx.save()
    ctx.globalAlpha = 0.9
    ctx.fillStyle = C.unlitBody
    ctx.beginPath()
    ctx.ellipse(x, y + 12, 17, 6, 0, 0, Math.PI * 2)
    ctx.fill()
    // the body: a tall wedge, blacker than the mask, with a cold rim
    ctx.globalAlpha = 1
    ctx.beginPath()
    ctx.moveTo(x - 13, y + 12)
    ctx.lineTo(x - 8, y + 12 - h)
    ctx.quadraticCurveTo(x, y + 2 - h - 8, x + 8, y + 12 - h)
    ctx.lineTo(x + 13, y + 12)
    ctx.closePath()
    ctx.fillStyle = C.unlitBody
    ctx.fill()
    ctx.globalAlpha = 0.22
    ctx.strokeStyle = '#2b3550'
    ctx.lineWidth = 2
    ctx.stroke()

    if (k > 0.5) {
      // the eyes. Two, pale, level, and they do not blink.
      const ey = y + 12 - h + 10
      const halt = u.tags.z4halt === 1
      ctx.globalAlpha = halt ? 0.55 + 0.3 * Math.sin(t * 12) : 0.92
      ctx.fillStyle = C.unlitEye
      ctx.fillRect(Math.round(x - 5), Math.round(ey), 3, 3)
      ctx.fillRect(Math.round(x + 3), Math.round(ey), 3, 3)
      ctx.globalAlpha = 0.16
      ctx.beginPath()
      ctx.arc(x, ey + 1, 15, 0, Math.PI * 2)
      ctx.fill()
    }

    // the tightening ring: the drone, made visible
    const pd = Math.hypot(x - run.player.pos.x, y - run.player.pos.y)
    if (pd < U.droneRange && mode === UN_WALK) {
      const near = 1 - pd / U.droneRange
      ctx.globalAlpha = 0.1 + 0.35 * near
      ctx.strokeStyle = '#6d7cab'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(x, y + 10, 22 + (1 - near) * 26 + Math.sin(t * 3) * 3, 0, Math.PI * 2)
      ctx.stroke()
    }
    ctx.restore()
  }

  /** The Mother's telegraphs and weather, which the dark may not eat either. */
  private renderArena(d: Draw): void {
    const ctx = d.ctx
    const t = this.anim
    ctx.save()
    for (const lane of arenaFx.lanes) {
      if (!lane.on) continue
      const dx = lane.x1 - lane.x0
      const dy = lane.y1 - lane.y0
      const len = Math.hypot(dx, dy) || 1
      const nx = -dy / len
      const ny = dx / len
      const hw = 30
      const charging = lane.k >= 0
      const k = charging ? lane.k : 1
      ctx.globalAlpha = charging ? 0.1 + 0.28 * k : 0.34
      ctx.fillStyle = charging ? '#ffd166' : '#ffb066'
      ctx.beginPath()
      ctx.moveTo(lane.x0 + nx * hw, lane.y0 + ny * hw)
      ctx.lineTo(lane.x1 + nx * hw, lane.y1 + ny * hw)
      ctx.lineTo(lane.x1 - nx * hw, lane.y1 - ny * hw)
      ctx.lineTo(lane.x0 - nx * hw, lane.y0 - ny * hw)
      ctx.closePath()
      ctx.fill()
      // the glint-trail: motes running the lane, so the direction is unmistakable
      ctx.globalAlpha = 0.55 + 0.4 * k
      ctx.fillStyle = '#fff0c4'
      for (let i = 0; i < 9; i++) {
        const f = ((t * 1.9 + i * 0.111) % 1) * len
        const s = Math.sin(t * 6 + i) * hw * 0.55
        ctx.fillRect(Math.round(lane.x0 + (dx / len) * f + nx * s), Math.round(lane.y0 + (dy / len) * f + ny * s), 3, 3)
      }
    }

    if (arenaFx.gust > 0) {
      ctx.globalAlpha = 0.2 + 0.4 * arenaFx.gust
      ctx.strokeStyle = '#e8dcbf'
      ctx.lineWidth = 3
      for (let i = 0; i < 4; i++) {
        const r = 90 + i * 90 + (1 - arenaFx.gust) * 260
        ctx.beginPath()
        ctx.arc(arenaFx.ghost.x, arenaFx.ghost.y, r, 0, Math.PI * 2)
        ctx.stroke()
      }
    }

    if (arenaFx.dustR > 0) {
      ctx.globalAlpha = 0.3 + 0.12 * Math.sin(t * 5)
      ctx.strokeStyle = '#8e7f6a'
      ctx.lineWidth = 8
      ctx.setLineDash([18, 12])
      ctx.beginPath()
      ctx.arc(zone4Lights.lampX, zone4Lights.lampY, arenaFx.dustR, 0, Math.PI * 2)
      ctx.stroke()
      ctx.setLineDash([])
    }

    if (arenaFx.seal.t > 0) {
      const k = arenaFx.seal.t
      ctx.globalAlpha = Math.min(0.8, k * 2)
      ctx.strokeStyle = '#6d7cab'
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(arenaFx.seal.x, arenaFx.seal.y, 46 + (0.5 - Math.min(0.5, k)) * 60, 0, Math.PI * 2)
      ctx.stroke()
      textAt.x = arenaFx.seal.x
      textAt.y = arenaFx.seal.y - 62
      d.text('SEALED — LIGHT A BRAZIER', textAt, 12, '#9fb0e0')
    }
    ctx.restore()
  }

  /** The channel bar, and the vault's shimmer when its brazier is burning. */
  private renderPrompts(d: Draw, run: RunCtx): void {
    const ctx = d.ctx
    if (this.channel >= 0 && this.channelNeed > 0.5) {
      const b = zone4Lights.braziers[this.channel]
      if (b) {
        const k = Math.min(1, this.channelT / this.channelNeed)
        ctx.save()
        ctx.globalAlpha = 0.9
        ctx.fillStyle = '#000000'
        ctx.fillRect(b.x - 26, b.y - 46, 52, 7)
        ctx.fillStyle = C.lamp
        ctx.fillRect(b.x - 25, b.y - 45, 50 * k, 5)
        ctx.restore()
      }
    }
    if (this.vaultDoors.length > 0 && this.vaultBrazier >= 0) {
      const lit = zone4Lights.braziers[this.vaultBrazier]?.lit === true
      ctx.save()
      for (const dr of this.vaultDoors) {
        const x = dr.tx * TILE + TILE / 2
        const y = dr.ty * TILE + TILE / 2
        if (lit) {
          ctx.globalAlpha = 0.35 + 0.3 * Math.sin(this.anim * 3.4 + dr.tx)
          ctx.fillStyle = '#ffd98a'
          ctx.fillRect(dr.tx * TILE + 5, dr.ty * TILE + 5, TILE - 10, TILE - 10)
          ctx.globalAlpha = 0.7
          ctx.strokeStyle = '#e3b23c'
          ctx.lineWidth = 2
          ctx.strokeRect(dr.tx * TILE + 2.5, dr.ty * TILE + 2.5, TILE - 5, TILE - 5)
        } else if (Math.hypot(x - run.player.pos.x, y - run.player.pos.y) < 130) {
          // sealed by the dark: a cold seam, and the reason, only when close
          ctx.globalAlpha = 0.28
          ctx.strokeStyle = '#4a5570'
          ctx.lineWidth = 1.5
          ctx.strokeRect(dr.tx * TILE + 6.5, dr.ty * TILE + 6.5, TILE - 13, TILE - 13)
          textAt.x = x
          textAt.y = y - TILE
          d.text('DARK — LIGHT THE BRAZIER', textAt, 10, '#6d7cab')
        }
      }
      ctx.restore()
    }
  }

  // --------------------------------------------------------------- test seam

  debugState(): {
    braziers: { x: number; y: number; lit: boolean; fuel: number }[]
    litCount: number
    lampRadius: number
    lampMul: number
    gloom: { base: number; factor: number; rateMul: number; state: string }
    unlit: { alive: boolean; x: number; y: number; mode: number; halt: boolean; banishT: number; timer: number }
    vault: { doors: number; brazier: number; lit: boolean; forced: boolean }
    inArena: boolean
    isRespite: boolean
    channel: { index: number; t: number; need: number }
    lightAtPlayer: number
  } {
    const u = this.unlit
    return {
      braziers: zone4Lights.braziers.map((b) => ({ x: b.x, y: b.y, lit: b.lit, fuel: b.fuel })),
      litCount: zone4Lights.litCount(),
      lampRadius: zone4Lights.lampRadius,
      lampMul: zone4Lights.lampMul,
      gloom: {
        base: this.gloomBase,
        factor: this.gloomFactor,
        rateMul: this.ctx ? this.ctx.gloom.rateMul : 0,
        state: this.gloomState,
      },
      unlit: {
        alive: u != null && u.alive,
        x: u ? u.pos.x : 0,
        y: u ? u.pos.y : 0,
        mode: u && typeof u.tags.z4mode === 'number' ? u.tags.z4mode : -1,
        halt: u ? u.tags.z4halt === 1 : false,
        banishT: this.banishT,
        timer: this.unlitTimer,
      },
      vault: {
        doors: this.vaultDoors.length,
        brazier: this.vaultBrazier,
        lit: this.vaultBrazier >= 0 ? zone4Lights.braziers[this.vaultBrazier]?.lit === true : false,
        forced: this.vaultForced,
      },
      inArena: this.inArena,
      isRespite: this.isRespite,
      channel: { index: this.channel, t: this.channelT, need: this.channelNeed },
      lightAtPlayer: this.ctx ? zone4Lights.lightAt(this.ctx.player.pos.x, this.ctx.player.pos.y) : 0,
    }
  }

  debugSpawnUnlit(): void {
    if (!this.ctx) return
    this.banishT = 0
    this.unlitTimer = 0
    this.spawnUnlit(this.ctx, false)
  }

  /** Light everything. The Mother's death calls this through relightAllBraziers. */
  relightAll(): void {
    if (!this.ctx) return
    for (let i = 0; i < zone4Lights.braziers.length; i++) this.light(this.ctx, i)
  }

  debugLightAll(): void {
    this.relightAll()
  }

  debugSetFuel(i: number, seconds: number): void {
    const b = zone4Lights.braziers[i]
    if (b) b.fuel = seconds
  }
}

const ROOM_BARKS = [
  'One source, and more braziers than you need. Learn what light costs.',
  'Something in here eats light. It is the one to kill first.',
  'Three sources, one behind iron — and the vault will not show itself in the dark.',
]

export const zone4Mechanic = new GloamingMines()

/**
 * TEST SEAM (test/zone4.mjs). Reading the light state and steering the fires
 * from a spec is the only way to assert a 20-second burn, a 45-second stalker
 * and a Gloom multiplier without waiting minutes a dozen times over. Inert
 * unless something calls it; nothing in the game does.
 */
export const zone4Debug = {
  state: (): ReturnType<GloamingMines['debugState']> => zone4Mechanic.debugState(),
  spawnUnlit: (): void => zone4Mechanic.debugSpawnUnlit(),
  lightAll: (): void => zone4Mechanic.debugLightAll(),
  setFuel: (i: number, s: number): void => zone4Mechanic.debugSetFuel(i, s),
  douse: (i: number): void => douseBrazier(i, false),
  lights: zone4Lights,
  arenaFx,
}

// ===========================================================================
// 4. THE FLOOR LAYER
// One canvas per room: rail lines down the long galleries, timber struts against
// the rock, and ore veins bleeding out of every wall face. Composited once at
// room build; almost all of it is invisible almost all of the time, which is the
// point — walking your lamp across it is how the room gets revealed.
// ===========================================================================

function paintFloorLayer(ctx: RunCtx, w: number, h: number): HTMLCanvasElement | null {
  if (w === 0 || h === 0) return null
  const c = document.createElement('canvas')
  c.width = w * TILE
  c.height = h * TILE
  const g = c.getContext('2d')
  if (!g) return null
  g.imageSmoothingEnabled = false

  const hash = (x: number, y: number): number => (((x * 73856093) ^ (y * 19349663)) >>> 0) % 1000

  // --- rails: any open horizontal run of 8+ tiles gets a track down its middle
  for (let ty = 1; ty < h - 1; ty++) {
    let run = 0
    for (let tx = 1; tx <= w - 1; tx++) {
      const open = tx < w - 1 && !ctx.isWall(tx, ty)
      if (open) {
        run++
        continue
      }
      if (run >= 8 && hash(tx, ty) % 100 < 46) {
        const x0 = (tx - run) * TILE
        const x1 = tx * TILE
        const y = ty * TILE + TILE / 2
        g.strokeStyle = C.rail
        g.lineWidth = 3
        g.beginPath()
        g.moveTo(x0, y - 5)
        g.lineTo(x1, y - 5)
        g.moveTo(x0, y + 5)
        g.lineTo(x1, y + 5)
        g.stroke()
        g.strokeStyle = C.railHi
        g.lineWidth = 1
        g.beginPath()
        g.moveTo(x0, y - 6)
        g.lineTo(x1, y - 6)
        g.moveTo(x0, y + 4)
        g.lineTo(x1, y + 4)
        g.stroke()
        g.fillStyle = C.strut
        for (let x = x0 + 6; x < x1 - 4; x += 13) g.fillRect(x, y - 8, 3, 17)
      }
      run = 0
    }
  }

  // --- struts: a timber frame standing against every south-facing rock face
  for (let ty = 0; ty < h - 1; ty++) {
    for (let tx = 1; tx < w - 1; tx++) {
      if (!ctx.isWall(tx, ty) || ctx.isWall(tx, ty + 1)) continue
      const q = hash(tx, ty)
      if (q % 100 >= 30) continue
      const x = tx * TILE + 4 + (q % 12)
      const y = (ty + 1) * TILE
      const len = 12 + (q % 16)
      g.fillStyle = C.strut
      g.fillRect(x - 2, y - 2, 4, len)
      g.fillStyle = C.strutHi
      g.fillRect(x - 2, y - 2, 1, len)
      g.fillRect(x - 5, y - 3, 10, 3)
    }
  }

  // --- ore: veins bleeding down out of the rock, and the glint points on them
  const want = L.ore.perRoom
  let placed = 0
  for (let ty = 0; ty < h - 1 && placed < want; ty++) {
    for (let tx = 1; tx < w - 1 && placed < want; tx++) {
      if (!ctx.isWall(tx, ty) || ctx.isWall(tx, ty + 1)) continue
      const q = hash(tx * 3 + 1, ty * 7 + 5)
      if (q % 100 >= 34) continue
      placed++
      const x = tx * TILE + 3 + (q % 24)
      const y = (ty + 1) * TILE - 2
      const len = 10 + (q % 22)
      g.strokeStyle = C.oreDim
      g.lineWidth = 3
      g.beginPath()
      g.moveTo(x, y)
      g.quadraticCurveTo(x + ((q % 9) - 4), y + len * 0.55, x + ((q % 13) - 6), y + len)
      g.stroke()
      g.strokeStyle = C.ore
      g.lineWidth = 1
      g.stroke()
      oreGlints.push({ x: x + ((q % 13) - 6), y: y + len, p: (q % 100) / 100 })
      oreGlints.push({ x, y: y + 2, p: ((q * 7) % 100) / 100 })
    }
  }

  return c
}

// ===========================================================================
// 5. SPRITES
// Chunky, dark-outlined, and built to read as a silhouette first: a hunched
// digger with a dead lamp, a winged croucher, a mote with wings, a tall faceless
// nothing, and three states of fire.
// ===========================================================================

function pstr(p: Record<string, number | string>, k: string, d: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : d
}
function pnum(p: Record<string, number | string>, k: string, d: number): number {
  const v = p[k]
  return typeof v === 'number' ? v : d
}

registerPainter('z4_delver', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const skin = pstr(p, 'skin', '#6b6152')
  const dark = shade(skin, -0.45)
  const cloth = pstr(p, 'cloth', '#3a3128')
  const lamp = pstr(p, 'lamp', '#ffc46b')

  // hunched: the whole body leans forward off a bent spine
  a.ellipse(cx, s * 0.92, s * 0.3, s * 0.06, '#0a0a10')
  a.rect(Math.round(cx - s * 0.2), Math.round(s * 0.72), 6, Math.round(s * 0.2), dark)
  a.rect(Math.round(cx + s * 0.1), Math.round(s * 0.72), 6, Math.round(s * 0.2), dark)
  a.ellipse(cx, s * 0.58, s * 0.28, s * 0.2, cloth)
  a.ellipse(cx, s * 0.54, s * 0.22, s * 0.15, skin)
  for (let i = 0; i < 3; i++) {
    a.hline(Math.round(cx - s * 0.18), Math.round(s * (0.5 + i * 0.07)), Math.round(s * 0.36), dark)
  }
  // long arms, one still holding a pick
  a.line(Math.round(cx - s * 0.26), Math.round(s * 0.5), Math.round(cx - s * 0.34), Math.round(s * 0.78), dark)
  a.line(Math.round(cx + s * 0.24), Math.round(s * 0.5), Math.round(cx + s * 0.36), Math.round(s * 0.74), dark)
  a.line(Math.round(cx + s * 0.36), Math.round(s * 0.74), Math.round(cx + s * 0.44), Math.round(s * 0.5), '#54452f')
  a.line(Math.round(cx + s * 0.44), Math.round(s * 0.5), Math.round(cx + s * 0.3), Math.round(s * 0.44), '#8d97a8')
  // head, low and forward
  a.ellipse(cx, s * 0.32, s * 0.17, s * 0.16, skin)
  a.ellipse(cx, s * 0.36, s * 0.1, s * 0.07, dark)
  a.set(Math.round(cx - s * 0.07), Math.round(s * 0.31), '#100c0a')
  a.set(Math.round(cx + s * 0.06), Math.round(s * 0.31), '#100c0a')
  a.set(Math.round(cx - s * 0.07), Math.round(s * 0.32), lamp)
  a.set(Math.round(cx + s * 0.06), Math.round(s * 0.32), lamp)
  // the miner's lamp on the brow — dead, and that is the joke
  a.rect(Math.round(cx - s * 0.09), Math.round(s * 0.18), Math.round(s * 0.18), 4, '#2a2b35')
  a.rect(Math.round(cx - s * 0.04), Math.round(s * 0.16), 4, 3, '#4a3a34')
})

registerPainter('z4_plume', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const dirt = pstr(p, 'dirt', '#4a4032')
  const hi = shade(dirt, 0.3)
  const lo = shade(dirt, -0.45)
  // a mound with spoil thrown off it — no body, just the ground moving
  a.ellipse(cx, s * 0.72, s * 0.36, s * 0.16, lo)
  a.ellipse(cx, s * 0.68, s * 0.3, s * 0.13, dirt)
  a.ellipse(cx, s * 0.63, s * 0.18, s * 0.08, hi)
  a.ellipse(cx, s * 0.66, s * 0.09, s * 0.05, '#100d0a')
  for (let i = -3; i <= 3; i++) {
    if (i === 0) continue
    const x = Math.round(cx + i * s * 0.11)
    a.set(x, Math.round(s * 0.5 + Math.abs(i) * 2), dirt)
    a.set(x + (i > 0 ? 1 : -1), Math.round(s * 0.44 + Math.abs(i) * 3), hi)
  }
})

registerPainter('z4_lampeater', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const wing = pstr(p, 'wing', '#c9a9e8')
  const wingLo = shade(wing, -0.5)
  const bodyC = pstr(p, 'body', '#3b2f4a')
  const eye = pstr(p, 'eye', '#ffe3a0')

  // the wings first, wide and ragged — the silhouette that says "priority"
  a.ellipse(cx - s * 0.3, s * 0.4, s * 0.22, s * 0.3, wingLo)
  a.ellipse(cx + s * 0.3, s * 0.4, s * 0.22, s * 0.3, wingLo)
  a.ellipse(cx - s * 0.29, s * 0.38, s * 0.17, s * 0.24, wing)
  a.ellipse(cx + s * 0.29, s * 0.38, s * 0.17, s * 0.24, wing)
  a.disc(cx - s * 0.3, s * 0.36, s * 0.06, wingLo)
  a.disc(cx + s * 0.3, s * 0.36, s * 0.06, wingLo)
  a.set(Math.round(cx - s * 0.3), Math.round(s * 0.36), eye)
  a.set(Math.round(cx + s * 0.3), Math.round(s * 0.36), eye)
  // the crouch: a squat body with its legs gathered under it
  a.ellipse(cx, s * 0.6, s * 0.16, s * 0.2, bodyC)
  a.ellipse(cx, s * 0.66, s * 0.1, s * 0.11, shade(bodyC, 0.25))
  a.rect(Math.round(cx - s * 0.14), Math.round(s * 0.78), 4, 5, shade(bodyC, -0.4))
  a.rect(Math.round(cx + s * 0.08), Math.round(s * 0.78), 4, 5, shade(bodyC, -0.4))
  // head and two greedy little eyes
  a.ellipse(cx, s * 0.34, s * 0.12, s * 0.11, bodyC)
  a.set(Math.round(cx - s * 0.06), Math.round(s * 0.33), eye)
  a.set(Math.round(cx + s * 0.05), Math.round(s * 0.33), eye)
  a.line(Math.round(cx - s * 0.06), Math.round(s * 0.25), Math.round(cx - s * 0.2), Math.round(s * 0.12), wing)
  a.line(Math.round(cx + s * 0.05), Math.round(s * 0.25), Math.round(cx + s * 0.19), Math.round(s * 0.12), wing)
})

registerPainter('z4_glintmoth', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const wing = pstr(p, 'wing', '#c9a9e8')
  const eye = pstr(p, 'eye', '#9fe8ff')
  const lo = shade(wing, -0.5)
  // tiny, four-winged, more mote than animal
  a.ellipse(cx - s * 0.24, s * 0.42, s * 0.2, s * 0.17, lo)
  a.ellipse(cx + s * 0.24, s * 0.42, s * 0.2, s * 0.17, lo)
  a.ellipse(cx - s * 0.22, s * 0.4, s * 0.15, s * 0.12, wing)
  a.ellipse(cx + s * 0.22, s * 0.4, s * 0.15, s * 0.12, wing)
  a.ellipse(cx - s * 0.16, s * 0.62, s * 0.11, s * 0.09, lo)
  a.ellipse(cx + s * 0.16, s * 0.62, s * 0.11, s * 0.09, lo)
  a.ellipse(cx, s * 0.5, s * 0.08, s * 0.22, '#2a2233')
  a.set(Math.round(cx - 1), Math.round(s * 0.34), eye)
  a.set(Math.round(cx + 1), Math.round(s * 0.34), eye)
  a.set(Math.round(cx), Math.round(s * 0.46), eye)
})

registerPainter('z4_unlit', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const eye = pstr(p, 'eye', '#cfe4ff')
  // The baked sprite is only ever the fallback — the mechanic paints it above
  // the mask so it can be blacker than the dark. Faithful anyway: a tall
  // faceless wedge with a miner's shoulders and two lights where a face is not.
  for (let y = Math.round(s * 0.16); y < s - 2; y++) {
    const t = (y - s * 0.16) / (s * 0.84)
    const w = Math.round(s * (0.1 + 0.26 * t))
    a.hline(Math.round(cx - w), y, w * 2, '#04050b')
  }
  a.ellipse(cx, s * 0.2, s * 0.14, s * 0.16, '#04050b')
  a.ellipse(cx, s * 0.34, s * 0.3, s * 0.06, '#020306')
  a.set(Math.round(cx - s * 0.06), Math.round(s * 0.2), eye)
  a.set(Math.round(cx + s * 0.05), Math.round(s * 0.2), eye)
  a.set(Math.round(cx - s * 0.06), Math.round(s * 0.21), eye)
  a.set(Math.round(cx + s * 0.05), Math.round(s * 0.21), eye)
})

// --- braziers: the same iron in three states --------------------------------

registerPainter('z4_brazier', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const hot = pnum(p, 'hot', 0)
  const iron = '#2f3341'
  const ironHi = '#565f75'
  // tripod
  a.line(Math.round(cx - s * 0.2), Math.round(s * 0.62), Math.round(cx - s * 0.3), Math.round(s * 0.92), iron)
  a.line(Math.round(cx + s * 0.2), Math.round(s * 0.62), Math.round(cx + s * 0.3), Math.round(s * 0.92), iron)
  a.line(Math.round(cx), Math.round(s * 0.66), Math.round(cx), Math.round(s * 0.94), iron)
  a.ellipse(cx, s * 0.94, s * 0.3, s * 0.05, '#0a0b12')
  // the bowl
  a.ellipse(cx, s * 0.58, s * 0.32, s * 0.16, iron)
  a.ellipse(cx, s * 0.52, s * 0.3, s * 0.12, shade(iron, -0.4))
  a.ellipse(cx, s * 0.5, s * 0.26, s * 0.09, hot > 0 ? shade('#ff8a3c', -0.2 + hot * 0.3) : '#191b24')
  a.ring(cx, s * 0.56, s * 0.32, ironHi)
  if (hot > 0) {
    a.ellipse(cx, s * 0.48, s * 0.18, s * 0.07, hot > 0.5 ? '#ffd08a' : '#7a4a2a')
    a.ellipse(cx, s * 0.4, s * 0.1, s * 0.12, hot > 0.5 ? '#ff8a3c' : '#4a3a34')
    if (hot > 0.5) {
      a.ellipse(cx, s * 0.34, s * 0.05, s * 0.08, '#fff0c4')
      a.set(Math.round(cx), Math.round(s * 0.26), '#fffbe8')
    }
  } else {
    // cold coals, and one that has not quite given up
    a.set(Math.round(cx - 2), Math.round(s * 0.5), '#4a3a34')
    a.set(Math.round(cx + 3), Math.round(s * 0.51), '#4a3a34')
  }
})

registerRecipe('z4_brazier_unlit', { key: 'z4_brazier', size: 30, params: { hot: 0 } })
registerRecipe('z4_brazier_lit', { key: 'z4_brazier', size: 30, params: { hot: 1 } })
registerRecipe('z4_brazier_gutter', { key: 'z4_brazier', size: 30, params: { hot: 0.4 } })
registerRecipe('z4_plume', { key: 'z4_plume', size: 30 })

// --- generators: three tiers, each keeping the violet source-rift ------------

function sourceMaw(a: PixelArt, cx: number, cy: number, rx: number, ry: number): void {
  a.ellipse(cx, cy, rx, ry, '#150c22')
  a.ellipse(cx, cy + 1, rx * 0.68, ry * 0.66, '#5b2f92')
  a.ellipse(cx, cy + 1, rx * 0.36, ry * 0.34, '#a774e8')
}

registerPainter('gen_collapsedshaft', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const rock = pstr(p, 'rock', '#3b4152')
  const lo = shade(rock, -0.45)
  const hi = shade(rock, 0.3)
  const timber = pstr(p, 'timber', '#3a2b20')

  // a caved-in adit: a broken headframe and a mouth of rubble
  a.ellipse(cx, s * 0.9, s * 0.44, s * 0.09, '#08090f')
  a.rect(Math.round(cx - s * 0.4), Math.round(s * 0.26), 5, Math.round(s * 0.6), timber)
  a.rect(Math.round(cx + s * 0.32), Math.round(s * 0.3), 5, Math.round(s * 0.56), timber)
  a.rect(Math.round(cx - s * 0.42), Math.round(s * 0.24), Math.round(s * 0.8), 5, shade(timber, 0.3))
  a.line(Math.round(cx - s * 0.36), Math.round(s * 0.3), Math.round(cx + s * 0.3), Math.round(s * 0.46), shade(timber, -0.3))
  a.ellipse(cx, s * 0.72, s * 0.36, s * 0.2, lo)
  a.ellipse(cx, s * 0.68, s * 0.3, s * 0.16, rock)
  for (let i = -3; i <= 3; i++) {
    const x = Math.round(cx + i * s * 0.1)
    a.rect(x - 2, Math.round(s * 0.6 + ((i * 7) % 5)), 5, 4, i % 2 === 0 ? hi : lo)
  }
  sourceMaw(a, cx, s * 0.66, s * 0.15, s * 0.11)
})

registerPainter('gen_cocooncluster', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const silk = pstr(p, 'silk', '#cbbfd8')
  const lo = shade(silk, -0.55)
  const glow = pstr(p, 'glow', '#9fe8ff')

  // a knot of cocoons slung off the roof, one of them already open
  a.rect(Math.round(cx - s * 0.36), 0, Math.round(s * 0.72), 4, lo)
  const pods: [number, number, number][] = [
    [-0.26, 0.4, 0.15],
    [0.04, 0.5, 0.19],
    [0.28, 0.36, 0.13],
    [-0.06, 0.72, 0.12],
  ]
  for (const [ox, oy, r] of pods) {
    a.line(Math.round(cx + ox * s), 3, Math.round(cx + ox * s), Math.round(s * (oy - r)), lo)
    a.ellipse(cx + ox * s, s * oy, s * r * 0.62, s * r, lo)
    a.ellipse(cx + ox * s, s * (oy - 0.01), s * r * 0.46, s * r * 0.82, silk)
    a.set(Math.round(cx + ox * s), Math.round(s * oy), glow)
  }
  sourceMaw(a, cx + 0.04 * s, s * 0.5, s * 0.1, s * 0.09)
})

registerPainter('gen_casteridol', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const stone = pstr(p, 'stone', '#33384a')
  const lo = shade(stone, -0.45)
  const hi = shade(stone, 0.32)
  const eye = pstr(p, 'eye', '#b98cff')

  // a hewn idol cut straight out of the rock face, one lit socket
  a.ellipse(cx, s * 0.92, s * 0.38, s * 0.07, '#08090f')
  a.rect(Math.round(cx - s * 0.28), Math.round(s * 0.34), Math.round(s * 0.56), Math.round(s * 0.56), stone)
  a.vline(Math.round(cx - s * 0.28), Math.round(s * 0.34), Math.round(s * 0.56), hi)
  a.vline(Math.round(cx + s * 0.26), Math.round(s * 0.34), Math.round(s * 0.56), lo)
  a.ellipse(cx, s * 0.3, s * 0.3, s * 0.18, stone)
  a.rect(Math.round(cx - s * 0.3), Math.round(s * 0.18), Math.round(s * 0.6), 4, hi)
  a.ellipse(cx, s * 0.3, s * 0.11, s * 0.08, '#0b0a14')
  a.ellipse(cx, s * 0.3, s * 0.06, s * 0.04, eye)
  a.set(Math.round(cx), Math.round(s * 0.29), '#efe6ff')
  for (let i = 0; i < 4; i++) {
    a.hline(Math.round(cx - s * 0.2), Math.round(s * (0.52 + i * 0.09)), Math.round(s * 0.4), lo)
  }
  sourceMaw(a, cx, s * 0.74, s * 0.12, s * 0.1)
})

// --- the Deep Caster's bolt: violet, because homing reads violet -------------

registerPainter('deepbolt', (a: PixelArt, p, _pal) => {
  const s = a.size
  const c = pstr(p, 'color', '#b98cff')
  a.disc(s / 2, s / 2, s * 0.36, shade(c, -0.35))
  a.disc(s / 2, s / 2, s * 0.26, c)
  a.disc(s / 2 - 1, s / 2 - 1, s * 0.13, '#f0e6ff')
  a.set(1, Math.round(s / 2), shade(c, -0.4))
  a.set(s - 2, Math.round(s / 2), shade(c, -0.4))
})

registerRecipe('deepbolt', { key: 'deepbolt', size: 15, params: { color: C.homing } })

// ===========================================================================
// registry
// ===========================================================================

registerZoneContent({
  enemies: ZONE4_ENEMIES,
  projectiles: ZONE4_PROJECTILES,
  generators: ZONE4_GENERATORS,
})
registerZone(ZONE4, zone4Mechanic)
